import { useContentsquareTracking } from "@/features/contentsquare/useContentsquareTracking";
import React, { Suspense, useEffect, useState } from "react";
// Stale-build safe replacement for React.lazy (see src/lib/lazyWithRetry.ts).
import { lazyWithRetry as lazy } from "@/lib/lazyWithRetry";

import { SUPABASE_URL, SUPABASE_PROJECT_ID } from "@/integrations/supabase/env";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useSearchParams, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { isAlternateHost } from "@/hooks/useNetworkBranding";
import { ShowsProvider } from "@/contexts/ShowsContext";
import { EpisodesProvider } from "@/contexts/EpisodesContext";
import { BugReportProvider } from "@/contexts/BugReportContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { FeatureAccessRoute } from "@/components/FeatureAccessRoute";
import { useShopAccess } from "@/hooks/useShopAccess";

import { RecoveryRedirect } from "@/components/auth/RecoveryRedirect";
// Background features are not needed by a first-time visitor on the marketing
// pages — keep them out of the entry bundle so mobile Safari can respond to
// taps sooner.

import { ImpersonationBanner } from "@/components/layout/ImpersonationBanner";
const RouteTooltips = lazy(() => import("@/features/tooltips/RouteTooltips").then(m => ({ default: m.RouteTooltips })));
const PopupHost = lazy(() => import("@/features/popups/PopupHost").then(m => ({ default: m.PopupHost })));


import { VersionUpdatePrompt } from "@/components/VersionUpdatePrompt";
import { StagingModeBanner } from "@/components/StagingModeBanner";
import { useGoogleAnalytics } from "@/hooks/useGoogleAnalytics";
import { useGoogleTagManager } from "@/hooks/useGoogleTagManager";

import { SeoManager } from "@/components/seo/SeoManager";

// Eagerly load the homepage — it's the landing page
import Index from "./pages/Index";

// Lazy-load everything else
// Lazy-load everything else


const EventInvite = lazy(() => import("@/pages/public/EventInvite"));
const Auth = lazy(() => import("@/pages/Auth"));
const EmbedTokens = lazy(() => import("@/pages/embed/EmbedTokens"));

/** Sends old /docs/... links to /knowledgebase/... */
function DocsRedirect() {
  const { pathname, search, hash } = useLocation();
  return <Navigate to={pathname.replace(/^\/docs/, "/knowledgebase") + search + hash} replace />;
}

/**
 * Any non-PODGO domain (e.g. portal.branded.network) goes straight to /auth
 * instead of PODGO's marketing page. Auth already sends signed-in users on
 * to their portal and brands itself by host.
 */
function HomeOrBrandedSignIn() {
  return isAlternateHost() ? <Navigate to="/auth" replace /> : <Index />;
}
const ContractReturn = lazy(() => import("@/pages/ContractReturn"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const ZoomCallback = lazy(() => import("@/pages/ZoomCallback"));
const Terms = lazy(() => import("@/pages/Terms"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const DMCA = lazy(() => import("@/pages/DMCA"));
const CookiePolicy = lazy(() => import("@/pages/CookiePolicy"));
const GDPRPage = lazy(() => import("@/pages/GDPR"));
const PlanSelection = lazy(() => import("@/pages/onboarding/PlanSelection"));
const NameCapture = lazy(() => import("@/pages/onboarding/NameCapture"));
const TeamInvite = lazy(() => import("@/pages/onboarding/TeamInvite"));
const AcceptInvite = lazy(() => import("@/pages/AcceptInvite"));
const RedeemInvite = lazy(() => import("@/pages/RedeemInvite"));
const SessionExpired = lazy(() => import("./pages/SessionExpired"));


// TEMP: demo-only onboarding prototype, remove after demo
const OnboardingDemo = () => {
  if (typeof window !== "undefined") {
    window.location.replace("/onboarding-demo/index.html");
  }
  return null;
};




const PublicShowPage = lazy(() => import("./pages/public/ShowPage"));
const PodcastDirectory = lazy(() => import("./pages/public/PodcastDirectory"));
const PodcastCategories = lazy(() => import("./pages/public/PodcastCategories"));
const PublicEpisodePage = lazy(() => import("./pages/public/EpisodePage"));
const SmartLinkRedirect = lazy(() => import("./pages/SmartLinkRedirect"));
const LinkRedirect = lazy(() => import("./pages/LinkRedirect"));
import { LinkCapture } from "@/features/links/LinkCapture";
import { PageViewTracker } from "@/features/links/tracking";
import { isLinksHost } from "@/features/links/api";
const PublicHelp = lazy(() => import("./pages/public/PublicHelp"));
const BlogFeed = lazy(() => import("./pages/public/BlogFeed"));
const BlogArticle = lazy(() => import("./pages/public/BlogArticle"));
const Partners = lazy(() => import("./pages/public/Partners"));
const PartnerDetail = lazy(() => import("./pages/public/PartnerDetail"));
const Studios = lazy(() => import("./pages/public/Studios"));
const Store = lazy(() => import("./pages/public/Store"));
const StoreProduct = lazy(() => import("./pages/public/StoreProduct"));
const StudioLocationPage = lazy(() => import("./pages/public/StudioLocationPage"));
const StudioRoomPage = lazy(() => import("./pages/public/StudioRoomPage"));
const StudioReservationThankYou = lazy(() => import("./pages/public/StudioReservationThankYou"));
const StudioBookingActionResult = lazy(() => import("./pages/public/StudioBookingActionResult"));
const Careers = lazy(() => import("./pages/public/Careers"));
const About = lazy(() => import("./pages/public/About"));
const WhyJoinPodgo = lazy(() => import("./pages/public/WhyJoinPodgo"));
const Focus = lazy(() => import("./pages/public/Focus"));
const FocusHowItWorks = lazy(() => import("./pages/public/FocusHowItWorks"));
const FocusPricing = lazy(() => import("./pages/public/FocusPricing"));
const FocusSignup = lazy(() => import("./pages/public/FocusSignup"));

const BookingPage = lazy(() => import("@/pages/public/BookingPage"));
const BookingManage = lazy(() => import("@/pages/public/BookingManage"));

// Route trees no anonymous visitor can reach — kept out of the entry bundle.
const AdminRoutes = lazy(() => import("./routes/AdminRoutes"));
const PortalRoutes = lazy(() => import("./routes/PortalRoutes"));



const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

const RouteLoadingFallback = () => (
  <div className="flex min-h-screen items-center justify-center bg-background" role="status" aria-label="Loading page">
    <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
  </div>
);

/**
 * Mounts background-only features (popups, guided tooltips, analytics tags)
 * after the browser is idle, so they never compete with the first paint or the
 * first tap on a phone. `afterLoad` holds off until the window `load` event as
 * well — the tag manager is pure third-party script work and was still landing
 * inside the first paint window on throttled phones.
 */
const DeferredUntilIdle = ({
  children,
  afterLoad,
  delayMs = 0,
}: {
  children: React.ReactNode;
  afterLoad?: boolean;
  /** Extra wait after load before going idle-hunting (analytics tags). */
  delayMs?: number;
}) => {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let idleId: number | undefined;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    let delayId: ReturnType<typeof setTimeout> | undefined;
    let interacted = false;

    const schedule = () => {
      const w = window as Window & {
        requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      };
      if (w.requestIdleCallback) {
        idleId = w.requestIdleCallback(() => setReady(true), { timeout: 3000 });
        return;
      }
      timeoutId = setTimeout(() => setReady(true), 1500);
    };

    // A real visitor interaction beats the timer — analytics shouldn't miss a
    // click just because the page is still settling.
    const onInteract = () => {
      if (interacted) return;
      interacted = true;
      setReady(true);
    };

    const afterDelay = () => {
      if (delayMs > 0) delayId = setTimeout(schedule, delayMs);
      else schedule();
    };

    const events: Array<keyof WindowEventMap> = ["pointerdown", "keydown", "scroll"];
    if (delayMs > 0) {
      events.forEach((e) => window.addEventListener(e, onInteract, { once: true, passive: true }));
    }

    if (afterLoad && document.readyState !== "complete") {
      window.addEventListener("load", afterDelay, { once: true });
    } else {
      afterDelay();
    }

    return () => {
      window.removeEventListener("load", afterDelay);
      events.forEach((e) => window.removeEventListener(e, onInteract));
      if (idleId !== undefined) (window as any).cancelIdleCallback?.(idleId);
      if (timeoutId) clearTimeout(timeoutId);
      if (delayId) clearTimeout(delayId);
    };
  }, [afterLoad, delayMs]);
  if (!ready) return null;
  return <Suspense fallback={null}>{children}</Suspense>;
};


const ProductionPreviewIndicator = () => {
  const isStandalonePreviewTarget = import.meta.env.VITE_PODGO_BACKEND_TARGET === "standalone-preview";
  const isPreviewOrDevHost = typeof window !== "undefined" && ["lovable.app", "localhost", "127.0.0.1"].some((host) => {
    const hostname = window.location.hostname;
    return hostname === host || hostname.endsWith(`.${host}`);
  });
  const backendHost = (() => {
    try {
      return new URL(SUPABASE_URL).hostname;
    } catch {
      return SUPABASE_PROJECT_ID || "unknown backend";
    }
  })();

  if (!isStandalonePreviewTarget || !isPreviewOrDevHost) {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 max-w-[min(20rem,calc(100vw-2rem))] rounded-md border border-destructive/30 bg-destructive px-3 py-2 text-xs font-semibold tracking-normal text-destructive-foreground shadow-lg">
      <div className="uppercase">Standalone preview backend</div>
      <div className="mt-0.5 truncate normal-case opacity-90">{backendHost}</div>
    </div>
  );
};

// Injects the admin-configured GA4 tag + Google Tag Manager container at
// runtime (must live inside QueryClientProvider and the Router).
const GoogleAnalytics = () => {
  useGoogleAnalytics();
  useGoogleTagManager();
  useContentsquareTracking();
  return null;
};


// App component with all providers
// Forwards /signup?<query> → /auth?tab=signup&<query>, preserving params like ?ref=CODE
const SignupRedirect = () => {
  const [params] = useSearchParams();
  const merged = new URLSearchParams(params);
  if (!merged.has("tab")) merged.set("tab", "signup");
  return <Navigate to={`/auth?${merged.toString()}`} replace />;
};




const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <AuthProvider>
            <Toaster />
            <Sonner />
            <ProductionPreviewIndicator />
            <VersionUpdatePrompt />
            <StagingModeBanner />
          <BrowserRouter>
        <ShowsProvider>
          <EpisodesProvider>
          <ImpersonationBanner />
          <DeferredUntilIdle>
            <RouteTooltips />
            {/* Admin-authored popups, evaluated against the current route/visitor. */}
            <PopupHost />
          </DeferredUntilIdle>
          {/* Analytics tags wait for load + idle: they block the main thread and
              nothing on screen depends on them. */}
          <DeferredUntilIdle afterLoad delayMs={8000}>
            <GoogleAnalytics />
          </DeferredUntilIdle>

          <LinkCapture />
          <PageViewTracker />
          <SeoManager />
          <RecoveryRedirect />
          <BugReportProvider>
          <Suspense fallback={<RouteLoadingFallback />}>
          {isLinksHost() ? (
          <Routes>
            {/* links.podgo.io serves System › Links slugs only. */}
            <Route path="/:slug" element={<LinkRedirect />} />
            <Route path="*" element={<LinkRedirect slugOverride="" />} />
          </Routes>
          ) : (
          <Routes>
            <Route path="/links/:slug" element={<LinkRedirect />} />
            {/* Public Routes */}
            <Route path="/" element={<HomeOrBrandedSignIn />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/event-invite" element={<EventInvite />} />
            <Route path="/embed/tokens" element={<EmbedTokens />} />
            {/* Branded sign-in link for a white-label network: /n/<slug> */}
            <Route path="/n/:networkSlug" element={<Auth />} />
            <Route path="/login" element={<Navigate to="/auth" replace />} />
            <Route path="/signup" element={<SignupRedirect />} />
            <Route path="/auth/reset-password" element={<ResetPassword />} />
            <Route path="/auth/zoom/callback" element={<ZoomCallback />} />
            <Route path="/session-expired" element={<SessionExpired />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/dmca" element={<DMCA />} />
            <Route path="/cookies" element={<CookiePolicy />} />
            <Route path="/gdpr" element={<GDPRPage />} />

            {/* Onboarding Routes (require auth but not full onboarding) */}
            <Route path="/onboarding/name" element={<NameCapture />} />
            <Route path="/onboarding/plan" element={<ProtectedRoute requirePlan={false}><PlanSelection /></ProtectedRoute>} />
            <Route path="/onboarding/team" element={<ProtectedRoute requirePlan={false}><TeamInvite /></ProtectedRoute>} />

            {/* Team Invitation Acceptance Route (token-based) */}
            <Route path="/invite/:token" element={<AcceptInvite />} />
            {/* Admin/Network-Admin Invitation Redemption (mints fresh auth token on click) */}
            <Route path="/invitation/:invitationId" element={<RedeemInvite />} />

            {/* DocuSign embedded signing return bridge */}
            <Route path="/contract-return" element={<ContractReturn />} />

            {/* Public Show Page */}
            <Route path="/podcasts" element={<PodcastDirectory />} />
            <Route path="/podcasts/page/:page" element={<PodcastDirectory />} />
            <Route path="/podcasts/categories" element={<PodcastCategories />} />
            <Route path="/podcasts/category/:categorySlug" element={<PodcastDirectory />} />
            <Route
              path="/podcasts/category/:categorySlug/page/:page"
              element={<PodcastDirectory />}
            />
            <Route path="/podcast/:showSlug" element={<PublicShowPage />} />
            <Route path="/podcast/:showSlug/:episodeSlug" element={<PublicEpisodePage />} />
            <Route path="/s/:slug" element={<SmartLinkRedirect />} />
            <Route path="/help" element={<PublicHelp />} />

            {/* Public scheduling */}
            <Route path="/booking/manage/:token" element={<BookingManage />} />
            <Route path="/booking/:slug" element={<BookingPage />} />
            <Route path="/blog" element={<BlogFeed />} />
            <Route path="/blog/:slug" element={<BlogArticle />} />
            <Route path="/knowledgebase" element={<BlogFeed section="docs" />} />
            <Route path="/knowledgebase/:slug" element={<BlogArticle section="docs" />} />
            {/* Old /docs links keep working. */}
            <Route path="/docs/*" element={<DocsRedirect />} />
            <Route path="/partners" element={<Partners />} />
            <Route path="/partners/:slug" element={<PartnerDetail />} />
            {/* Storefront is signed-in only for now, and follows the Shop visibility setting. */}
            <Route
              path="/store"
              element={
                <ProtectedRoute requirePlan={false}>
                  <FeatureAccessRoute useAccess={useShopAccess}>
                    <Store />
                  </FeatureAccessRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/store/:slug"
              element={
                <ProtectedRoute requirePlan={false}>
                  <FeatureAccessRoute useAccess={useShopAccess}>
                    <StoreProduct />
                  </FeatureAccessRoute>
                </ProtectedRoute>
              }
            />
            <Route path="/studios" element={<Studios />} />
            <Route path="/studios/:locationSlug" element={<StudioLocationPage />} />
            <Route path="/studios/:locationSlug/:roomSlug" element={<StudioRoomPage />} />
            <Route path="/studios/reservation/:id" element={<StudioReservationThankYou />} />
            <Route path="/studio-booking-action" element={<StudioBookingActionResult />} />
            <Route path="/careers" element={<Careers />} />
            <Route path="/about" element={<About />} />
            <Route path="/why-you-should-join-podgo" element={<WhyJoinPodgo />} />
            <Route path="/focus" element={<Focus />} />
            <Route path="/focus/how-it-works" element={<FocusHowItWorks />} />
            <Route path="/focus/pricing" element={<FocusPricing />} />
            <Route path="/focus/signup" element={<FocusSignup />} />

            {/* TEMP: demo-only onboarding prototype, remove after demo */}
            <Route path="/onboarding-demo" element={<OnboardingDemo />} />

            {/* Admin console — one lazy chunk, never loaded for anonymous visitors */}
            <Route path="/admin/*" element={<AdminRoutes />} />

            {/* Signed-in portal + 404 — one lazy chunk, loaded on demand */}
            <Route path="*" element={<PortalRoutes />} />

          </Routes>
          )}
          </Suspense>
          </BugReportProvider>
          </EpisodesProvider>
        </ShowsProvider>
          </BrowserRouter>
      </AuthProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
